// frontend/src/utils/graficaCleaver.js
//
// Dibuja la Gráfica III del Cleaver (T = M - L) en un <canvas>, con el mismo
// formato de la plantilla impresa: columnas D, I, S, C con su escala,
// intensidad 1-28 a la izquierda, segmentos 1-7 a la derecha, banda media
// sombreada (segmento 4), la línea que une los cuatro puntos, los números de
// segmento y el patrón clásico.
//
// La usan la pantalla de resultados (ResultsView) y el reporte PDF
// (reportService), para que ambos se vean idénticos. Los datos vienen del
// backend: resultado.cleaver.grafica_iii (backend/src/services/cleaverScoring.js).

const FACTORES = ['D', 'I', 'S', 'C'];

const COLOR = {
  marco: '#048377',
  marcoOscuro: '#036b61',
  banda: 'rgba(4, 131, 119, 0.16)',
  lineaSegmento: 'rgba(4, 131, 119, 0.45)',
  texto: '#2d3748',
  textoClaro: '#718096',
  escala: '#4a5568',
  perfil: '#172B35',
  blanco: '#ffffff',
};

// Tamaño lógico del dibujo; el canvas se escala con `escala` para nitidez
export const ANCHO_GRAFICA = 420;
export const ALTO_GRAFICA = 570;

export const dibujarGraficaCleaver = (canvas, grafica, escala = 2) => {
  if (!canvas || !grafica?.puntos || !grafica?.escalas) return;
  canvas.width = ANCHO_GRAFICA * escala;
  canvas.height = ALTO_GRAFICA * escala;
  const ctx = canvas.getContext('2d');
  ctx.setTransform(escala, 0, 0, escala, 0, 0);
  ctx.clearRect(0, 0, ANCHO_GRAFICA, ALTO_GRAFICA);
  ctx.fillStyle = COLOR.blanco;
  ctx.fillRect(0, 0, ANCHO_GRAFICA, ALTO_GRAFICA);

  const fuente = (peso, tam) => `${peso} ${tam}px Inter, "Segoe UI", Arial, sans-serif`;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // ---- Área de la escala ----
  const top = 42;
  const bottom = 458;
  const alturaFila = (bottom - top) / 28;
  const yDe = (intensidad) => top + (28 - intensidad) * alturaFila + alturaFila / 2;
  const xIntensidad = 6;
  const anchoLateral = 30;
  const xSegmento = ANCHO_GRAFICA - anchoLateral - 6;
  const inicioColumnas = xIntensidad + anchoLateral + 8;
  const anchoColumna = (xSegmento - 8 - inicioColumnas) / 4;
  // La línea vertical de cada factor va un poco a la derecha del centro y
  // la escala impresa a su izquierda, como en la plantilla
  const xLinea = (k) => inicioColumnas + anchoColumna * k + anchoColumna * 0.62;

  // Banda media (segmento 4 = intensidad 13-16)
  ctx.fillStyle = COLOR.banda;
  ctx.fillRect(inicioColumnas, yDe(16) - alturaFila / 2, xSegmento - 8 - inicioColumnas, alturaFila * 4);

  // Líneas punteadas entre segmentos (entre las intensidades 4|5, 8|9, ...)
  ctx.strokeStyle = COLOR.lineaSegmento;
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  [4, 8, 12, 16, 20, 24].forEach(n => {
    const y = yDe(n) - alturaFila / 2;
    ctx.beginPath();
    ctx.moveTo(inicioColumnas, y);
    ctx.lineTo(xSegmento - 8, y);
    ctx.stroke();
  });
  ctx.setLineDash([]);

  // Columna de intensidad (izquierda) y de segmentos (derecha)
  ctx.fillStyle = COLOR.marco;
  ctx.fillRect(xIntensidad, top - 4, anchoLateral, bottom - top + 8);
  ctx.fillRect(xSegmento, top - 4, anchoLateral, bottom - top + 8);
  ctx.fillStyle = COLOR.blanco;
  ctx.font = fuente(600, 9.5);
  for (let n = 1; n <= 28; n++) ctx.fillText(String(n), xIntensidad + anchoLateral / 2, yDe(n));
  ctx.font = fuente(700, 18);
  for (let s = 1; s <= 7; s++) ctx.fillText(String(s), xSegmento + anchoLateral / 2, yDe(4 * s - 1.5));
  ctx.font = fuente(700, 8);
  ctx.fillStyle = COLOR.marcoOscuro;
  ctx.fillText('INTENSIDAD', xIntensidad + anchoLateral / 2 + 8, top - 12);
  ctx.fillText('SEGMENTO', xSegmento + anchoLateral / 2 - 4, top - 12);

  // Encabezados D I S C y escalas impresas
  FACTORES.forEach((f, k) => {
    const x = xLinea(k);
    ctx.fillStyle = COLOR.marco;
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(x - 17, 6, 34, 26, 5) : ctx.rect(x - 17, 6, 34, 26);
    ctx.fill();
    ctx.fillStyle = COLOR.blanco;
    ctx.font = fuente(800, 18);
    ctx.textAlign = 'center';
    ctx.fillText(f === 'I' ? 'i' : f, x, 19);

    ctx.strokeStyle = COLOR.marco;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, top - 4);
    ctx.lineTo(x, bottom + 4);
    ctx.stroke();

    ctx.fillStyle = COLOR.escala;
    ctx.font = fuente(500, 9.5);
    ctx.textAlign = 'right';
    (grafica.escalas[f] || []).forEach(([t, intensidad]) => {
      ctx.fillText(t > 0 ? `+${t}` : String(t), x - 6, yDe(intensidad));
    });
  });

  // ---- Perfil: línea y puntos ----
  const puntos = FACTORES.map((f, k) => ({ x: xLinea(k), y: yDe(grafica.puntos[f].intensidad) }));
  ctx.strokeStyle = COLOR.perfil;
  ctx.lineWidth = 2.5;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  puntos.forEach((p, k) => (k === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.stroke();
  puntos.forEach(p => {
    ctx.fillStyle = COLOR.perfil;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = COLOR.blanco;
    ctx.lineWidth = 2;
    ctx.stroke();
  });

  // ---- Números de segmentos y patrón clásico ----
  const yNumeros = bottom + 18;
  const altoCaja = 40;
  ctx.fillStyle = COLOR.marco;
  ctx.fillRect(6, yNumeros, ANCHO_GRAFICA - 12, altoCaja + 10);
  FACTORES.forEach((f, k) => {
    const x = xLinea(k);
    ctx.fillStyle = COLOR.blanco;
    ctx.fillRect(x - 24, yNumeros + 5, 48, altoCaja);
    ctx.fillStyle = COLOR.texto;
    ctx.font = fuente(700, 20);
    ctx.textAlign = 'center';
    ctx.fillText(String(grafica.puntos[f].segmento), x, yNumeros + 5 + altoCaja / 2);
  });
  // Etiquetas de la derecha, centradas en el espacio libre junto al borde
  const xEtiqueta = ANCHO_GRAFICA - 28;
  ctx.fillStyle = COLOR.blanco;
  ctx.font = fuente(600, 7.5);
  ctx.textAlign = 'center';
  ['Números', 'de', 'segmentos'].forEach((linea, k) => ctx.fillText(linea, xEtiqueta, yNumeros + 15 + k * 10));

  const yPatron = yNumeros + altoCaja + 16;
  ctx.fillStyle = COLOR.marco;
  ctx.fillRect(6, yPatron, ANCHO_GRAFICA - 12, 38);
  ctx.fillStyle = COLOR.blanco;
  ctx.fillRect(inicioColumnas - 4, yPatron + 5, xSegmento - inicioColumnas - 4, 28);
  ctx.fillStyle = COLOR.texto;
  ctx.font = fuente(700, 14);
  ctx.fillText(grafica.patron ? `Patrón ${grafica.patron}` : 'Patrón no disponible', (inicioColumnas + xSegmento - 8) / 2, yPatron + 19);
  ctx.fillStyle = COLOR.blanco;
  ctx.font = fuente(600, 7.5);
  ['Patrón', 'clásico'].forEach((linea, k) => ctx.fillText(linea, xEtiqueta, yPatron + 14 + k * 10));
};

// PNG de la gráfica para el reporte PDF (canvas fuera del DOM)
export const generarImagenGraficaCleaver = (grafica) => {
  if (!grafica?.puntos) return null;
  const canvas = document.createElement('canvas');
  dibujarGraficaCleaver(canvas, grafica, 3);
  return canvas.toDataURL('image/png');
};
